import React, { useEffect, useMemo, useRef, useState } from 'react';

// Kam vedú CTA tlačidlá (appka beží na samostatnej doméne).
// Prepíš cez REACT_APP_APP_URL pri builde ak treba.
const APP_URL = (process.env.REACT_APP_APP_URL || 'https://app.schvalovaniefaktur.sk').replace(/\/+$/, '');
const LOGIN_URL = `${APP_URL}/login`;
// „Skúsiť zadarmo" vedie na samoregistráciu (Free plán), nie na login —
// nový zákazník sa nevie prihlásiť, kým si nezaloží účet (hybrid: žiadosť → aktivácia).
const REGISTER_URL = `${APP_URL}/register`;

// ── Analytics (Google tag + Consent Mode v2) ──────────────────────────────
// Celé je inertné, kým nie je nastavené REACT_APP_GA_ID (napr. G-XXXXXXX).
// Bez ID sa nenačíta žiadny skript ani cookie. S ID: Consent Mode v2 je
// predvolene "denied" (EÚ) a marketingové/analytické cookies sa zapnú až
// po kliknutí "Súhlasím" v lište. Voľba sa pamätá v localStorage.
const GA_ID = (process.env.REACT_APP_GA_ID || '').trim();
const CONSENT_KEY = 'sf-consent-v1';
type ConsentChoice = 'granted' | 'denied';

function gtag(..._args: any[]) {
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  // POZOR: gtag.js spracuje príkaz iba ak sa do dataLayer pushne `arguments`
  // objekt — NIE bežné pole. Pushnutie poľa Google ticho ignoruje.
  // eslint-disable-next-line prefer-rest-params
  w.dataLayer.push(arguments);
}
function getStoredConsent(): ConsentChoice | null {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    return v === 'granted' || v === 'denied' ? v : null;
  } catch { return null; }
}
let gaBootstrapped = false;
function initAnalytics() {
  if (!GA_ID || gaBootstrapped) return;
  gaBootstrapped = true;
  const s = getStoredConsent() === 'granted' ? 'granted' : 'denied';
  gtag('consent', 'default', {
    ad_storage: s, ad_user_data: s, ad_personalization: s, analytics_storage: s,
    wait_for_update: 500,
  });
  gtag('js', new Date());
  gtag('config', GA_ID, { anonymize_ip: true });
  const el = document.createElement('script');
  el.async = true;
  el.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(el);
}
function setConsent(choice: ConsentChoice) {
  try { localStorage.setItem(CONSENT_KEY, choice); } catch {}
  if (!GA_ID) return;
  const s = choice;
  gtag('consent', 'update', {
    ad_storage: s, ad_user_data: s, ad_personalization: s, analytics_storage: s,
  });
}
// Konverzný/akčný event. Bez GA_ID je to no-op (nič sa neposiela).
function track(name: string, params?: Record<string, any>) {
  if (!GA_ID) return;
  gtag('event', name, params || {});
}

function ConsentBanner() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (GA_ID && getStoredConsent() === null) setVisible(true);
  }, []);
  if (!visible) return null;
  const choose = (c: ConsentChoice) => { setConsent(c); setVisible(false); };
  return (
    <div className="consent" role="dialog" aria-label="Súhlas s cookies" aria-live="polite">
      <p className="consent-text">
        Používame cookies na meranie návštevnosti a kvalitu reklamy. Analytické a marketingové
        cookies zapneme len s vaším súhlasom. Nevyhnutné cookies fungujú vždy.
      </p>
      <div className="consent-btns">
        <button className="btn btn-ghost" onClick={() => choose('denied')}>Len nevyhnutné</button>
        <button className="btn btn-primary" onClick={() => choose('granted')}>Súhlasím</button>
      </div>
    </div>
  );
}

const styles = `
  /* === THEME — LIGHT (default) === */
  :root {
    --bg: #fbfcfe;
    --bg-2: #f1f5f9;
    --surface: #ffffff;
    --surface-2: #f8fafc;
    --surface-elev: #ffffff;
    --border: rgba(15, 23, 42, 0.08);
    --border-strong: rgba(15, 23, 42, 0.14);
    --text: #0b1220;
    --text-strong: #050a18;
    --muted: #5b6478;
    --accent: #2563eb;
    --accent-2: #1d4ed8;
    --accent-soft: rgba(37, 99, 235, 0.08);
    --success: #16a34a;
    --warn: #d97706;
    --danger: #dc2626;
    --grid-line: rgba(15, 23, 42, 0.05);
    --glow-blue: rgba(59, 130, 246, 0.18);
    --glow-purple: rgba(168, 85, 247, 0.12);
    --glow-green: rgba(34, 197, 94, 0.08);
    --soon-bg: rgba(217, 119, 6, 0.10);
    --soon-fg: #b45309;
    --mock-bg: linear-gradient(180deg, #ffffff 0%, #fafbfc 100%);
    --mock-side: #f6f8fb;
    --mock-table: #ffffff;
    --mock-row-hover: rgba(37, 99, 235, 0.05);
    --mock-row-expanded: rgba(37, 99, 235, 0.07);
    --shadow-card: 0 1px 3px rgba(15,23,42,0.04), 0 10px 30px -12px rgba(15,23,42,0.12);
    --shadow-mock: 0 30px 80px -28px rgba(15,23,42,0.22), 0 0 0 1px rgba(15,23,42,0.04);
    --shadow-cta: 0 8px 24px rgba(37, 99, 235, 0.30);
    --shadow-cta-hover: 0 12px 28px rgba(37, 99, 235, 0.40);
    --pill-ok-bg: rgba(22, 163, 74, 0.10);
    --pill-ok-fg: #15803d;
    --pill-pending-bg: rgba(217, 119, 6, 0.12);
    --pill-pending-fg: #b45309;
    --pill-review-bg: rgba(37, 99, 235, 0.10);
    --pill-review-fg: #1d4ed8;
    --pill-over-bg: rgba(220, 38, 38, 0.10);
    --pill-over-fg: #b91c1c;
    --grad-1: #2563eb;
    --grad-2: #7c3aed;
    --grad-3: #0891b2;
    --nav-bg: rgba(255, 255, 255, 0.78);
    --hero-grid-mask: linear-gradient(180deg, rgba(0,0,0,0.6), transparent 70%);
  }

  /* === THEME — DARK === */
  :root[data-theme="dark"] {
    --bg: #0b1220;
    --bg-2: #0f172a;
    --surface: #111a2e;
    --surface-2: #1a2542;
    --surface-elev: #14213d;
    --border: rgba(148, 163, 184, 0.14);
    --border-strong: rgba(148, 163, 184, 0.24);
    --text: #e5edff;
    --text-strong: #ffffff;
    --muted: #9aa8c7;
    --accent: #3b82f6;
    --accent-2: #60a5fa;
    --accent-soft: rgba(59, 130, 246, 0.12);
    --success: #22c55e;
    --warn: #f59e0b;
    --danger: #ef4444;
    --grid-line: rgba(148, 163, 184, 0.06);
    --glow-blue: rgba(59, 130, 246, 0.25);
    --glow-purple: rgba(168, 85, 247, 0.18);
    --glow-green: rgba(34, 197, 94, 0.10);
    --soon-bg: rgba(245, 158, 11, 0.12);
    --soon-fg: #fbbf24;
    --mock-bg: linear-gradient(180deg, rgba(26,37,66,0.85) 0%, rgba(17,26,46,0.85) 100%);
    --mock-side: rgba(11,18,32,0.6);
    --mock-table: rgba(11,18,32,0.6);
    --mock-row-hover: rgba(59, 130, 246, 0.10);
    --mock-row-expanded: rgba(59, 130, 246, 0.14);
    --shadow-card: 0 4px 20px -8px rgba(0,0,0,0.6);
    --shadow-mock: 0 30px 80px -20px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04) inset;
    --shadow-cta: 0 8px 24px rgba(37, 99, 235, 0.35);
    --shadow-cta-hover: 0 12px 28px rgba(37, 99, 235, 0.45);
    --pill-ok-bg: rgba(34,197,94,0.12);
    --pill-ok-fg: #4ade80;
    --pill-pending-bg: rgba(245,158,11,0.14);
    --pill-pending-fg: #fbbf24;
    --pill-review-bg: rgba(59,130,246,0.14);
    --pill-review-fg: #60a5fa;
    --pill-over-bg: rgba(239,68,68,0.14);
    --pill-over-fg: #f87171;
    --grad-1: #60a5fa;
    --grad-2: #a78bfa;
    --grad-3: #22d3ee;
    --nav-bg: rgba(11, 18, 32, 0.78);
    --hero-grid-mask: linear-gradient(180deg, black, transparent 70%);
  }

  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body.landing-body {
    background: var(--bg);
    color: var(--text);
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    -webkit-font-smoothing: antialiased;
    line-height: 1.55;
    transition: background-color .2s ease, color .2s ease;
  }
  .landing { min-height: 100vh; background: var(--bg); color: var(--text); }
  .container { max-width: 1180px; margin: 0 auto; padding: 0 24px; }

  /* Nav */
  .nav {
    position: sticky; top: 0; z-index: 50;
    background: var(--nav-bg);
    backdrop-filter: saturate(180%) blur(12px);
    -webkit-backdrop-filter: saturate(180%) blur(12px);
    border-bottom: 1px solid var(--border);
  }
  .nav-inner { display: flex; align-items: center; justify-content: space-between; padding: 14px 0; gap: 16px; }
  .brand { display: flex; align-items: center; gap: 10px; text-decoration: none; color: var(--text); font-weight: 700; letter-spacing: -0.01em; }
  .brand-mark {
    width: 32px; height: 32px; border-radius: 9px;
    background: linear-gradient(135deg, var(--accent) 0%, var(--accent-2) 100%);
    display: grid; place-items: center;
    box-shadow: 0 6px 18px rgba(37, 99, 235, 0.35);
  }
  .brand-mark svg { width: 18px; height: 18px; color: white; }
  .brand-text { font-size: 17px; }
  .brand-text .dot { color: var(--accent); }
  .nav-links { display: flex; gap: 28px; align-items: center; }
  .nav-links a { color: var(--muted); text-decoration: none; font-size: 14.5px; font-weight: 500; transition: color .15s ease; white-space: nowrap; }
  @media (max-width: 1120px) { .nav-links { gap: 18px; } }
  .nav-links a:hover { color: var(--text); }
  .nav-cta { display: flex; gap: 10px; align-items: center; }
  .theme-toggle {
    width: 38px; height: 38px; border-radius: 10px;
    border: 1px solid var(--border); background: var(--surface);
    color: var(--text);
    display: grid; place-items: center; cursor: pointer;
    transition: all .15s ease;
  }
  .theme-toggle:hover { border-color: var(--border-strong); background: var(--surface-2); }
  .theme-toggle svg { width: 18px; height: 18px; }

  /* Hamburger */
  .hamburger {
    display: none;
    width: 38px; height: 38px; border-radius: 10px;
    border: 1px solid var(--border); background: var(--surface);
    cursor: pointer; place-items: center;
  }
  .hamburger:hover { border-color: var(--border-strong); background: var(--surface-2); }
  .hb-box { position: relative; width: 18px; height: 14px; display: block; }
  .hb-box i {
    position: absolute; left: 0; width: 18px; height: 2px; border-radius: 2px;
    background: var(--text);
    transition: transform .22s ease, opacity .18s ease, top .22s ease;
  }
  .hb-box i:nth-child(1) { top: 0; }
  .hb-box i:nth-child(2) { top: 6px; }
  .hb-box i:nth-child(3) { top: 12px; }
  .hb-box.open i:nth-child(1) { top: 6px; transform: rotate(45deg); }
  .hb-box.open i:nth-child(2) { opacity: 0; }
  .hb-box.open i:nth-child(3) { top: 6px; transform: rotate(-45deg); }

  /* Mobile menu */
  .mobile-menu {
    overflow: hidden;
    max-height: 0;
    border-top: 1px solid transparent;
    transition: max-height .32s cubic-bezier(.4,0,.2,1), border-color .3s ease;
    background: var(--bg);
  }
  .mobile-menu.open { max-height: 560px; border-top-color: var(--border); box-shadow: 0 24px 40px -20px rgba(15,23,42,0.25); }
  .mm-inner { display: flex; flex-direction: column; padding: 10px 24px 22px; }
  .mm-inner a {
    color: var(--text); text-decoration: none; font-weight: 600; font-size: 16px;
    padding: 14px 4px; border-bottom: 1px solid var(--border);
  }
  .mm-inner a:active { color: var(--accent); }
  .mm-cta { display: flex; flex-direction: column; gap: 10px; margin-top: 18px; }

  @media (max-width: 880px) {
    .nav-links { display: none; }
    .nav-desktop-only { display: none !important; }
    .hamburger { display: grid; }
  }
  @media (min-width: 881px) {
    .mobile-menu { display: none; }
  }

  /* Buttons */
  .btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; padding: 11px 18px; border-radius: 10px; font-weight: 600; font-size: 14.5px; text-decoration: none; cursor: pointer; border: 1px solid transparent; transition: transform .12s ease, background .15s ease, box-shadow .15s ease, border-color .15s ease; white-space: nowrap; font-family: inherit; }
  .btn-primary { background: linear-gradient(135deg, var(--accent) 0%, var(--accent-2) 100%); color: white; box-shadow: var(--shadow-cta); }
  .btn-primary:hover { transform: translateY(-1px); box-shadow: var(--shadow-cta-hover); }
  .btn-ghost { background: transparent; color: var(--text); border-color: var(--border-strong); }
  .btn-ghost:hover { background: var(--surface-2); border-color: var(--border-strong); }
  .btn-lg { padding: 14px 22px; font-size: 15.5px; border-radius: 12px; }
  .btn-block { width: 100%; }

  /* Hero */
  .hero { position: relative; padding: 90px 0 110px; overflow: hidden; }
  .hero::before {
    content: ''; position: absolute; inset: -40% -20% auto -20%; height: 80%;
    background:
      radial-gradient(50% 50% at 50% 30%, var(--glow-blue) 0%, transparent 60%),
      radial-gradient(35% 40% at 80% 10%, var(--glow-purple) 0%, transparent 60%),
      radial-gradient(35% 40% at 15% 0%, var(--glow-green) 0%, transparent 60%);
    z-index: 0;
    pointer-events: none;
  }
  .hero::after {
    content: ''; position: absolute; inset: 0;
    background-image:
      linear-gradient(var(--grid-line) 1px, transparent 1px),
      linear-gradient(90deg, var(--grid-line) 1px, transparent 1px);
    background-size: 44px 44px;
    mask-image: radial-gradient(ellipse at 50% 0%, black 0%, transparent 70%);
    -webkit-mask-image: radial-gradient(ellipse at 50% 0%, black 0%, transparent 70%);
    z-index: 0;
    pointer-events: none;
  }
  .hero-inner { position: relative; z-index: 1; text-align: center; max-width: 880px; margin: 0 auto; }
  .badge { display: inline-flex; align-items: center; gap: 8px; padding: 6px 12px; border-radius: 999px; background: var(--accent-soft); border: 1px solid rgba(37,99,235,0.30); color: var(--accent); font-size: 13px; font-weight: 600; }
  .badge .pulse { width: 6px; height: 6px; border-radius: 50%; background: var(--success); box-shadow: 0 0 0 0 rgba(22,163,74,0.7); animation: pulse 1.8s infinite; }
  @keyframes pulse {
    0% { box-shadow: 0 0 0 0 rgba(22,163,74,0.7); }
    70% { box-shadow: 0 0 0 8px rgba(22,163,74,0); }
    100% { box-shadow: 0 0 0 0 rgba(22,163,74,0); }
  }
  h1.hero-title {
    margin: 22px 0 18px;
    font-size: clamp(36px, 6vw, 64px);
    line-height: 1.04;
    letter-spacing: -0.03em;
    font-weight: 800;
    color: var(--text-strong);
  }
  .hero-title .grad {
    background: linear-gradient(135deg, var(--grad-1) 0%, var(--grad-2) 50%, var(--grad-3) 100%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }
  .hero-sub { color: var(--muted); font-size: clamp(16px, 1.6vw, 19px); max-width: 680px; margin: 0 auto 32px; }
  .hero-cta { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }
  .hero-trust { margin-top: 22px; display: flex; gap: 18px; justify-content: center; flex-wrap: wrap; color: var(--muted); font-size: 13.5px; }
  .hero-trust span { display: inline-flex; align-items: center; gap: 6px; }
  .check { color: var(--success); font-weight: 700; }

  /* Mockup */
  .hero-mock { position: relative; z-index: 1; margin-top: 60px; max-width: 1080px; margin-left: auto; margin-right: auto; }
  .mock-hint { text-align: center; color: var(--muted); font-size: 13px; margin-top: 14px; }
  .mock-hint .kbd { background: var(--surface-2); border: 1px solid var(--border); padding: 1px 6px; border-radius: 5px; font-family: ui-monospace, SFMono-Regular, monospace; font-size: 11.5px; color: var(--text); }
  .mock-card {
    background: var(--mock-bg);
    border: 1px solid var(--border);
    border-radius: 18px;
    padding: 18px;
    box-shadow: var(--shadow-mock);
  }
  .mock-bar { display: flex; align-items: center; gap: 8px; padding: 6px 8px 14px; border-bottom: 1px solid var(--border); margin-bottom: 14px; }
  .mock-dot { width: 11px; height: 11px; border-radius: 50%; }
  .mock-dot.r { background: #ef4444; } .mock-dot.y { background: #f59e0b; } .mock-dot.g { background: #22c55e; }
  .mock-url { margin-left: 12px; color: var(--muted); font-size: 12.5px; font-family: ui-monospace, SFMono-Regular, monospace; }
  .mock-grid { display: grid; grid-template-columns: 240px 1fr; gap: 18px; }
  @media (max-width: 720px) { .mock-grid { grid-template-columns: 1fr; } .mock-side { display: none; } }
  .mock-side { background: var(--mock-side); border: 1px solid var(--border); border-radius: 12px; padding: 14px; }
  .mock-side h5 { font-size: 11px; color: var(--muted); text-transform: uppercase; letter-spacing: 0.08em; margin: 0 0 10px; }
  .mock-side h5.sub { margin-top: 18px; }
  .mock-side ul { list-style: none; padding: 0; margin: 0; font-size: 14px; color: var(--muted); display: flex; flex-direction: column; gap: 4px; }
  .mock-side li { padding: 7px 9px; border-radius: 8px; cursor: pointer; transition: background .12s ease, color .12s ease; display: flex; align-items: center; justify-content: space-between; gap: 6px; }
  .mock-side li:hover { background: var(--mock-row-hover); color: var(--text); }
  .mock-side li.active { color: var(--text-strong); background: var(--accent-soft); font-weight: 600; }
  .mock-side li.passive { cursor: default; }
  .mock-side li.passive:hover { background: transparent; color: var(--muted); }
  .badge-count { background: var(--surface-2); color: var(--muted); font-size: 11px; padding: 1px 7px; border-radius: 999px; font-weight: 600; }
  .mock-side li.active .badge-count { background: rgba(37,99,235,0.18); color: var(--accent); }

  .mock-table { background: var(--mock-table); border: 1px solid var(--border); border-radius: 12px; overflow: hidden; }
  .mock-row { display: grid; grid-template-columns: 1.4fr 1fr 1fr 110px; gap: 12px; padding: 12px 14px; border-bottom: 1px solid var(--border); align-items: center; font-size: 13.5px; transition: background .12s ease; }
  .mock-row.header { color: var(--muted); font-size: 11.5px; text-transform: uppercase; letter-spacing: 0.08em; background: var(--surface-2); cursor: default; }
  .mock-row.clickable { cursor: pointer; }
  .mock-row.clickable:hover { background: var(--mock-row-hover); }
  .mock-row.expanded { background: var(--mock-row-expanded); }
  .mock-row:last-of-type { border-bottom: none; }
  .mock-empty { padding: 28px 18px; text-align: center; color: var(--muted); font-size: 13.5px; }
  .pill { display: inline-flex; padding: 3px 10px; border-radius: 999px; font-size: 11.5px; font-weight: 600; white-space: nowrap; }
  .pill.ok { background: var(--pill-ok-bg); color: var(--pill-ok-fg); }
  .pill.pending { background: var(--pill-pending-bg); color: var(--pill-pending-fg); }
  .pill.review { background: var(--pill-review-bg); color: var(--pill-review-fg); }
  .pill.over { background: var(--pill-over-bg); color: var(--pill-over-fg); }
  @media (max-width: 720px) { .mock-row { grid-template-columns: 1.2fr 1fr 90px; } .mock-row .col-due { display: none; } }

  .mock-detail {
    padding: 14px 18px 18px;
    background: var(--mock-row-expanded);
    border-bottom: 1px solid var(--border);
    animation: fadeIn .18s ease;
  }
  @keyframes fadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; } }
  .mock-detail-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; margin-bottom: 14px; }
  @media (max-width: 720px) { .mock-detail-grid { grid-template-columns: 1fr 1fr; } }
  .mock-detail-grid .span-2 { grid-column: span 2; }
  .mock-detail-grid label { display: block; color: var(--muted); font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 4px; font-weight: 600; }
  .mock-detail-grid span { color: var(--text); font-size: 13.5px; font-weight: 500; }
  .mock-approval {
    display: inline-flex; align-items: center; gap: 8px;
    font-size: 13px; font-weight: 600;
    padding: 9px 13px; border-radius: 9px; margin-bottom: 14px;
  }
  .mock-approval strong { font-weight: 800; }
  .mock-approval.ok { background: var(--pill-ok-bg); color: var(--pill-ok-fg); }
  .mock-approval.review { background: var(--pill-review-bg); color: var(--pill-review-fg); }
  .mock-approval.wait { background: var(--pill-pending-bg); color: var(--pill-pending-fg); }
  .mock-actions { display: flex; gap: 10px; flex-wrap: wrap; }
  .mock-btn { padding: 8px 14px; border-radius: 8px; font-size: 13px; font-weight: 600; border: 1px solid transparent; cursor: pointer; font-family: inherit; transition: all .12s ease; }
  .mock-btn.primary { background: var(--success); color: white; }
  .mock-btn.primary:hover { filter: brightness(1.05); transform: translateY(-1px); }
  .mock-btn.danger { background: transparent; color: var(--danger); border-color: var(--pill-over-bg); }
  .mock-btn.danger:hover { background: var(--pill-over-bg); }
  .mock-btn.ghost { background: var(--surface-2); color: var(--text); border-color: var(--border); }
  .mock-btn.ghost:hover { border-color: var(--border-strong); }

  /* Sections */
  section { padding: 88px 0; position: relative; }
  .eyebrow { color: var(--accent); font-weight: 700; text-transform: uppercase; letter-spacing: 0.12em; font-size: 12.5px; }
  h2.section-title { font-size: clamp(28px, 3.6vw, 42px); letter-spacing: -0.02em; line-height: 1.12; margin: 12px 0 14px; font-weight: 800; color: var(--text-strong); }
  .section-lead { color: var(--muted); font-size: 17px; max-width: 680px; }
  .section-head { text-align: center; max-width: 760px; margin: 0 auto 56px; }
  .section-head .section-lead { margin: 0 auto; }

  /* Stats */
  .stats { padding: 40px 0; border-top: 1px solid var(--border); border-bottom: 1px solid var(--border); background: var(--surface-2); }
  .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 28px; }
  .stat .num { font-size: clamp(28px, 3vw, 36px); font-weight: 800; letter-spacing: -0.02em; }
  .stat .num .grad { background: linear-gradient(135deg, var(--grad-1), var(--grad-2)); -webkit-background-clip: text; background-clip: text; color: transparent; }
  .stat .lbl { color: var(--muted); font-size: 13.5px; margin-top: 4px; }
  @media (max-width: 720px) { .stats-grid { grid-template-columns: repeat(2, 1fr); gap: 24px; } }

  /* Feature grid */
  .features-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
  @media (min-width: 981px) { .features-grid.four { grid-template-columns: repeat(4, 1fr); } }
  @media (max-width: 980px) { .features-grid { grid-template-columns: repeat(2, 1fr); } }
  .features-extra { text-align: center; color: var(--muted); font-size: 14.5px; margin: 28px auto 0; max-width: 760px; }
  @media (max-width: 620px) { .features-grid { grid-template-columns: 1fr; } }
  .feature {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 14px;
    padding: 22px;
    transition: transform .15s ease, border-color .15s ease, box-shadow .15s ease;
    box-shadow: var(--shadow-card);
  }
  .feature:hover { transform: translateY(-3px); border-color: rgba(37,99,235,0.4); }
  .feature-icon {
    width: 42px; height: 42px; border-radius: 10px;
    background: var(--accent-soft); color: var(--accent);
    display: grid; place-items: center;
    margin-bottom: 14px;
  }
  .feature-icon.soon { background: var(--soon-bg); color: var(--soon-fg); }
  .feature-icon svg { width: 22px; height: 22px; }
  .feature h3 { margin: 0 0 8px; font-size: 17px; font-weight: 700; letter-spacing: -0.01em; color: var(--text-strong); }
  .feature p { margin: 0; color: var(--muted); font-size: 14.5px; }
  .feature .tag-live { display: inline-block; margin-top: 12px; padding: 2px 8px; font-size: 11px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--pill-ok-fg); background: var(--pill-ok-bg); border-radius: 999px; }
  .feature .tag-soon { display: inline-block; margin-top: 12px; padding: 2px 8px; font-size: 11px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--soon-fg); background: var(--soon-bg); border-radius: 999px; }
  .feature.soon-featured {
    position: relative;
    border-color: rgba(37,99,235,0.5);
    background: linear-gradient(180deg, var(--accent-soft), var(--surface));
    box-shadow: 0 20px 44px -22px rgba(37,99,235,0.45), var(--shadow-card);
  }
  .feature.soon-featured .feature-icon { background: linear-gradient(135deg, var(--accent), var(--accent-2)); color: #fff; }
  .soon-ribbon {
    position: absolute; top: -11px; right: 16px;
    background: linear-gradient(135deg, var(--accent), var(--accent-2));
    color: #fff; font-size: 11px; font-weight: 700; letter-spacing: 0.05em;
    padding: 4px 10px; border-radius: 999px;
    box-shadow: 0 8px 18px rgba(37,99,235,0.4);
  }

  /* Process */
  .process-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 22px; }
  @media (max-width: 880px) { .process-grid { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 520px) { .process-grid { grid-template-columns: 1fr; } }
  .step { position: relative; padding: 22px; border: 1px solid var(--border); border-radius: 14px; background: var(--surface); box-shadow: var(--shadow-card); }
  .step-num { position: absolute; top: -16px; left: 20px; width: 34px; height: 34px; border-radius: 10px; background: linear-gradient(135deg, var(--accent), var(--accent-2)); color: white; font-weight: 800; display: grid; place-items: center; box-shadow: 0 8px 22px rgba(37,99,235,0.45); }
  .step h4 { margin: 14px 0 6px; font-size: 16px; font-weight: 700; color: var(--text-strong); }
  .step p { margin: 0; color: var(--muted); font-size: 14px; }

  /* Pricing */
  .pricing-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 18px; align-items: stretch; }
  @media (max-width: 1080px) { .pricing-grid { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 560px) { .pricing-grid { grid-template-columns: 1fr; } }
  .plan {
    display: flex; flex-direction: column;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 16px;
    padding: 26px 22px;
    position: relative;
    box-shadow: var(--shadow-card);
  }
  .plan.featured {
    border: 1px solid rgba(37,99,235,0.55);
    box-shadow: 0 30px 60px -20px rgba(37,99,235,0.30), 0 0 0 1px rgba(37,99,235,0.18) inset;
    transform: translateY(-4px);
  }
  .plan-name { font-size: 13px; font-weight: 700; color: var(--accent); letter-spacing: 0.08em; text-transform: uppercase; }
  .plan-price { display: flex; align-items: baseline; gap: 6px; margin: 10px 0 6px; }
  .plan-price .val { font-size: 40px; font-weight: 800; letter-spacing: -0.02em; color: var(--text-strong); }
  .plan-price .per { color: var(--muted); font-size: 14px; }
  .plan-tag { color: var(--muted); font-size: 13.5px; min-height: 38px; }
  .plan ul { list-style: none; padding: 16px 0 0; margin: 16px 0 0; border-top: 1px solid var(--border); display: flex; flex-direction: column; gap: 10px; flex: 1; }
  .plan ul li { display: flex; gap: 10px; font-size: 14px; color: var(--text); }
  .plan ul li svg { width: 18px; height: 18px; color: var(--success); flex-shrink: 0; margin-top: 2px; }
  .plan ul li .muted { color: var(--muted); }
  .plan .plan-cta { margin-top: 20px; }
  .ribbon { position: absolute; top: -12px; right: 18px; background: linear-gradient(135deg, #f59e0b, #ef4444); color: white; font-size: 11.5px; font-weight: 700; padding: 4px 10px; border-radius: 999px; letter-spacing: 0.06em; text-transform: uppercase; box-shadow: 0 8px 18px rgba(239,68,68,0.4); }
  .pricing-note { margin-top: 28px; text-align: center; color: var(--muted); font-size: 13.5px; }
  .billing-toggle { display: inline-flex; padding: 4px; border: 1px solid var(--border); border-radius: 999px; gap: 4px; margin: 0 auto 32px; background: var(--surface); }
  .billing-toggle button { background: transparent; border: none; color: var(--muted); padding: 8px 16px; border-radius: 999px; font-weight: 600; font-size: 13.5px; cursor: pointer; font-family: inherit; }
  .billing-toggle button.active { background: var(--accent); color: white; box-shadow: 0 6px 16px rgba(37,99,235,0.35); }
  .save-pill { background: var(--pill-ok-bg); color: var(--pill-ok-fg); font-size: 11.5px; padding: 2px 7px; border-radius: 999px; margin-left: 4px; font-weight: 700; }

  .enterprise {
    margin-top: 22px;
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 20px;
    align-items: center;
    padding: 24px 28px;
    border-radius: 16px;
    background: var(--surface);
    border: 1px solid var(--border);
    box-shadow: var(--shadow-card);
  }
  @media (max-width: 720px) { .enterprise { grid-template-columns: 1fr; } }

  /* FAQ */
  .faq { max-width: 820px; margin: 0 auto; }
  .faq details {
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 16px 20px;
    margin-bottom: 10px;
    background: var(--surface);
    box-shadow: var(--shadow-card);
  }
  .faq details[open] { border-color: rgba(37,99,235,0.35); }
  .faq summary { cursor: pointer; font-weight: 600; list-style: none; display: flex; justify-content: space-between; align-items: center; gap: 14px; color: var(--text-strong); }
  .faq summary::-webkit-details-marker { display: none; }
  .faq summary::after { content: '+'; color: var(--accent); font-size: 22px; font-weight: 400; line-height: 1; }
  .faq details[open] summary::after { content: '–'; }
  .faq .answer { margin-top: 10px; color: var(--muted); font-size: 14.5px; }

  /* CTA */
  .final-cta {
    position: relative;
    border-radius: 24px;
    padding: 60px 32px;
    text-align: center;
    overflow: hidden;
    background:
      radial-gradient(70% 100% at 50% 0%, rgba(59,130,246,0.20) 0%, transparent 70%),
      var(--surface);
    border: 1px solid rgba(37,99,235,0.35);
    margin: 0 auto;
    box-shadow: var(--shadow-card);
  }
  .final-cta h2 { font-size: clamp(28px, 4vw, 42px); margin: 0 0 12px; letter-spacing: -0.02em; color: var(--text-strong); }
  .final-cta p { color: var(--muted); margin: 0 auto 26px; max-width: 560px; }

  /* Footer */
  footer { border-top: 1px solid var(--border); padding: 48px 0 32px; color: var(--muted); font-size: 14px; background: var(--surface-2); }
  .footer-grid { display: grid; grid-template-columns: 1.4fr repeat(3, 1fr); gap: 36px; padding-bottom: 28px; }
  @media (max-width: 820px) { .footer-grid { grid-template-columns: 1fr 1fr; } }
  .footer-grid h6 { color: var(--text-strong); margin: 0 0 12px; font-size: 13px; text-transform: uppercase; letter-spacing: 0.08em; }
  .footer-grid a { color: var(--muted); text-decoration: none; display: block; padding: 4px 0; }
  .footer-grid a:hover { color: var(--text); }
  .footer-bottom { display: flex; justify-content: space-between; align-items: center; padding-top: 22px; border-top: 1px solid var(--border); flex-wrap: wrap; gap: 12px; font-size: 13px; }

  /* Segments — "Pre koho to je" */
  .segments-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 18px; }
  @media (max-width: 980px) { .segments-grid { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 560px) { .segments-grid { grid-template-columns: 1fr; } }
  .segment { background: var(--surface); border: 1px solid var(--border); border-radius: 14px; padding: 22px; box-shadow: var(--shadow-card); transition: transform .15s ease, border-color .15s ease; }
  .segment:hover { transform: translateY(-3px); border-color: rgba(37,99,235,0.4); }
  .segment .seg-emoji { font-size: 26px; }
  .segment h3 { margin: 12px 0 6px; font-size: 16.5px; font-weight: 700; color: var(--text-strong); }
  .segment p { margin: 0; color: var(--muted); font-size: 14px; }
  .segment .seg-plan { display: inline-block; margin-top: 12px; font-size: 11.5px; font-weight: 700; color: var(--accent); background: var(--accent-soft); padding: 2px 9px; border-radius: 999px; }

  /* Integrations strip */
  .logos-wrap { text-align: center; }
  .logos-strip { display: flex; flex-wrap: wrap; gap: 12px; justify-content: center; margin-top: 26px; }
  .logo-badge { display: inline-flex; align-items: center; gap: 8px; padding: 10px 18px; border-radius: 12px; background: var(--surface); border: 1px solid var(--border); font-weight: 700; font-size: 14.5px; color: var(--text); box-shadow: var(--shadow-card); }
  .logo-badge .soon-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--soon-fg); }
  .logo-badge .live-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--success); }
  .logos-note { margin-top: 18px; color: var(--muted); font-size: 13px; }

  /* Comparison — Predtým vs. S nami */
  .compare-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
  @media (max-width: 760px) { .compare-grid { grid-template-columns: 1fr; } }
  .compare-col { border-radius: 16px; padding: 26px; border: 1px solid var(--border); box-shadow: var(--shadow-card); }
  .compare-col.before { background: var(--surface); }
  .compare-col.after { background: linear-gradient(180deg, var(--accent-soft), var(--surface)); border-color: rgba(37,99,235,0.4); }
  .compare-col h3 { margin: 0 0 4px; font-size: 18px; font-weight: 800; color: var(--text-strong); }
  .compare-col .ch-sub { color: var(--muted); font-size: 13px; margin: 0 0 18px; }
  .compare-col ul { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 13px; }
  .compare-col li { display: flex; gap: 11px; font-size: 14.5px; color: var(--text); align-items: flex-start; }
  .compare-col li .ic { width: 20px; height: 20px; border-radius: 50%; flex-shrink: 0; display: grid; place-items: center; font-size: 12px; font-weight: 800; margin-top: 1px; }
  .compare-col.before li .ic { background: var(--pill-over-bg); color: var(--pill-over-fg); }
  .compare-col.info { background: var(--surface); }
  .compare-col.info li .ic { background: var(--soon-bg); color: var(--soon-fg); }
  .compare-col.after li .ic { background: var(--pill-ok-bg); color: var(--pill-ok-fg); }
  .compare-col.before li { color: var(--muted); }

  /* Testimonials */
  .testi-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
  @media (max-width: 920px) { .testi-grid { grid-template-columns: 1fr; max-width: 560px; margin: 0 auto; } }
  .testi { background: var(--surface); border: 1px solid var(--border); border-radius: 16px; padding: 26px; box-shadow: var(--shadow-card); display: flex; flex-direction: column; }
  .testi .stars { color: #f59e0b; font-size: 15px; letter-spacing: 2px; margin-bottom: 12px; }
  .testi blockquote { margin: 0 0 20px; font-size: 15.5px; line-height: 1.6; color: var(--text); flex: 1; }
  .testi-who { display: flex; align-items: center; gap: 12px; }
  .testi-avatar { width: 42px; height: 42px; border-radius: 50%; display: grid; place-items: center; font-weight: 800; color: white; font-size: 15px; flex-shrink: 0; }
  .testi-name { font-weight: 700; font-size: 14px; color: var(--text-strong); }
  .testi-role { color: var(--muted); font-size: 12.5px; }

  /* Security */
  .security-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; }
  @media (max-width: 880px) { .security-grid { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 540px) { .security-grid { grid-template-columns: 1fr; } }
  .sec-item { display: flex; gap: 14px; padding: 20px; border: 1px solid var(--border); border-radius: 14px; background: var(--surface); box-shadow: var(--shadow-card); }
  .sec-item .sec-icon { width: 40px; height: 40px; border-radius: 10px; background: var(--accent-soft); color: var(--accent); display: grid; place-items: center; flex-shrink: 0; }
  .sec-item .sec-icon svg { width: 20px; height: 20px; }
  .sec-item h4 { margin: 0 0 4px; font-size: 15px; font-weight: 700; color: var(--text-strong); }
  .sec-item p { margin: 0; color: var(--muted); font-size: 13.5px; }

  /* Scroll reveal */
  .reveal { opacity: 0; transform: translateY(24px); transition: opacity .6s cubic-bezier(.16,1,.3,1), transform .6s cubic-bezier(.16,1,.3,1); }
  .reveal.in { opacity: 1; transform: none; }
  /* Keep hover snappy on cards that also use .reveal (higher specificity wins) */
  .features-grid .feature, .segments-grid .segment, .testi-grid .testi,
  .security-grid .sec-item, .process-grid .step, .pricing-grid .plan,
  .logos-strip .logo-badge {
    transition: transform .18s cubic-bezier(.16,1,.3,1), border-color .18s ease, box-shadow .18s ease, opacity .55s ease;
  }
  @media (prefers-reduced-motion: reduce) {
    .reveal { opacity: 1 !important; transform: none !important; transition: none !important; }
  }

  /* Hero entrance */
  @keyframes heroIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: none; } }
  .hero-inner > * { animation: heroIn .7s cubic-bezier(.16,1,.3,1) both; }
  .hero-inner > *:nth-child(1) { animation-delay: .04s; }
  .hero-inner > *:nth-child(2) { animation-delay: .11s; }
  .hero-inner > *:nth-child(3) { animation-delay: .18s; }
  .hero-inner > *:nth-child(4) { animation-delay: .25s; }
  .hero-inner > *:nth-child(5) { animation-delay: .32s; }
  .hero-mock { animation: heroIn .85s cubic-bezier(.16,1,.3,1) both; animation-delay: .42s; }

  /* Micro-interactions */
  .feature-icon { transition: transform .28s cubic-bezier(.16,1,.3,1); }
  .feature:hover .feature-icon { transform: scale(1.1) rotate(-4deg); }
  .btn svg { transition: transform .15s ease; }
  .btn-primary:hover svg { transform: translateX(3px); }
  .seg-emoji, .step-num { transition: transform .2s ease; }
  .segment:hover .seg-emoji { transform: scale(1.12) rotate(-3deg); }
  .step:hover .step-num { transform: scale(1.1); }
  .logo-badge { transition: transform .15s ease, border-color .15s ease; }
  .logo-badge:hover { transform: translateY(-2px); border-color: rgba(37,99,235,0.45); }
  .testi { transition: transform .15s ease, border-color .15s ease; }
  .testi:hover { transform: translateY(-3px); border-color: rgba(37,99,235,0.35); }
  .sec-item { transition: transform .15s ease, border-color .15s ease; }
  .sec-item:hover { transform: translateY(-2px); border-color: rgba(37,99,235,0.32); }
  .theme-toggle svg { transition: transform .3s cubic-bezier(.16,1,.3,1); }
  .theme-toggle:hover svg { transform: rotate(18deg) scale(1.08); }

  /* Smooth theme transition (only surfaces without transform-hover) */
  .nav, footer, .final-cta, .billing-toggle, .mock-card, .stats {
    transition: background-color .25s ease, border-color .25s ease, color .25s ease;
  }

  @media (prefers-reduced-motion: reduce) {
    .hero-inner > *, .hero-mock { animation: none !important; }
    .feature:hover .feature-icon, .segment:hover .seg-emoji, .step:hover .step-num,
    .btn-primary:hover svg, .theme-toggle:hover svg { transform: none !important; }
  }

  /* Mobile polish */
  @media (max-width: 760px) {
    .enterprise .btn { width: 100%; }
  }
  @media (max-width: 640px) {
    .container { padding: 0 18px; }
    section { padding: 58px 0; }
    .hero { padding: 44px 0 56px; }
    .hero-mock { margin-top: 36px; }
    .section-head { margin-bottom: 36px; }
    .section-lead { font-size: 15.5px; }
    .nav-inner { padding: 12px 0; gap: 10px; }
    .brand-text { font-size: 15px; }
    .brand-mark { width: 30px; height: 30px; }
    .hero-trust { gap: 8px 14px; font-size: 12.5px; margin-top: 18px; }
    .hero-cta .btn-lg { width: 100%; }
    .hero-cta { gap: 10px; }
    .final-cta { padding: 40px 20px; border-radius: 18px; }
    .final-cta > div { flex-direction: column; }
    .final-cta .btn-lg { width: 100%; }
    .mock-card { padding: 12px; border-radius: 14px; }
    .mock-bar { padding: 4px 6px 12px; }
    .mock-url { font-size: 11px; }
    .mock-row { font-size: 12.5px; padding: 11px 12px; gap: 8px; }
    .mock-detail { padding: 12px 12px 14px; }
    .mock-hint { font-size: 12px; }
    .compare-col { padding: 20px; }
    .plan { padding: 22px 18px; }
    .pricing-note { font-size: 12.5px; }
    .testi { padding: 22px; }
    .footer-bottom { flex-direction: column; align-items: flex-start; }
  }
  @media (max-width: 380px) {
    .brand-text { font-size: 13.5px; }
    h1.hero-title { font-size: 33px; }
  }

  /* Misc */
  .section-divider { height: 1px; background: linear-gradient(90deg, transparent, var(--border), transparent); margin: 0; }

  /* Cookie consent */
  .consent { position: fixed; left: 16px; right: 16px; bottom: 16px; z-index: 200; display: flex; align-items: center; justify-content: space-between; gap: 18px; flex-wrap: wrap; max-width: 880px; margin: 0 auto; padding: 16px 20px; background: var(--surface-elev); border: 1px solid var(--border-strong); border-radius: 16px; box-shadow: 0 18px 50px rgba(2, 8, 23, 0.22); }
  .consent-text { margin: 0; font-size: 13.5px; color: var(--muted); line-height: 1.5; flex: 1 1 360px; }
  .consent-btns { display: flex; gap: 10px; flex-shrink: 0; }
  .consent-btns .btn { padding: 9px 16px; font-size: 14px; }
  @media (max-width: 560px) { .consent { flex-direction: column; align-items: stretch; } .consent-btns { justify-content: stretch; } .consent-btns .btn { flex: 1; } }
`;

function Icon({ path, size = 22 }: { path: string; size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d={path} />
    </svg>
  );
}

const ICONS = {
  flow: 'M4 6h10M4 12h16M4 18h7 M18 9l3 3-3 3 M14 15l3-3-3-3',
  scan: 'M4 7V5a1 1 0 0 1 1-1h2 M17 4h2a1 1 0 0 1 1 1v2 M20 17v2a1 1 0 0 1-1 1h-2 M7 20H5a1 1 0 0 1-1-1v-2 M4 12h16',
  users: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2 M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8 M22 21v-2a4 4 0 0 0-3-3.87 M16 3.13a4 4 0 0 1 0 7.75',
  bell: 'M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9 M13.73 21a2 2 0 0 1-3.46 0',
  shield: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z M9 12l2 2 4-4',
  bolt: 'M13 2L3 14h9l-1 8 10-12h-9l1-8z',
  building: 'M3 21h18 M5 21V7l8-4v18 M19 21V11l-6-4 M9 9v.01 M9 13v.01 M9 17v.01 M14 13v.01 M14 17v.01',
  check: 'M20 6L9 17l-5-5',
  database: 'M4 6c0-1.66 3.58-3 8-3s8 1.34 8 3v12c0 1.66-3.58 3-8 3s-8-1.34-8-3V6z M4 6c0 1.66 3.58 3 8 3s8-1.34 8-3 M4 12c0 1.66 3.58 3 8 3s8-1.34 8-3',
  calendar: 'M3 5h18v16H3z M3 9h18 M8 3v4 M16 3v4',
  globe: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z M2 12h20 M12 2a15 15 0 0 1 0 20 M12 2a15 15 0 0 0 0 20',
  cpu: 'M5 5h14v14H5z M9 9h6v6H9z M9 1v4 M15 1v4 M9 19v4 M15 19v4 M1 9h4 M1 15h4 M19 9h4 M19 15h4',
  mobile: 'M7 2h10a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z M11 18h2',
  link: 'M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 1 0-7.07-7.07L11.7 4.7 M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 1 0 7.07 7.07L12.3 19.3',
  card: 'M3 6h18v12H3z M3 10h18',
  chat: 'M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7A8.38 8.38 0 0 1 4 11.5 8.5 8.5 0 1 1 21 11.5z',
  file: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M16 13H8 M16 17H8 M10 9H8',
  receipt: 'M4 2h16v20l-4-2-4 2-4-2-4 2zM8 7h8M8 11h8M8 15h5',
  search: 'M11 17a6 6 0 1 0 0-12 6 6 0 0 0 0 12z M21 21l-4.35-4.35',
  upload: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M17 8l-5-5-5 5 M12 3v12',
};

export const FEATURES_LIVE = [
  { icon: ICONS.flow, title: 'Schvaľovanie faktúr', desc: 'Z e-mailu, od e-poštára aj z mobilu. Schválite na klik, aj na viac úrovní. Vždy viete, na koho schválenie doklad čaká.' },
  { icon: ICONS.cpu, title: 'AI čítanie dokladov', desc: 'Dodávateľa, sumy, DPH, položky aj IBAN vyčíta sama. Nič neprepisujete.' },
  { icon: ICONS.mobile, title: 'Bločky z mobilu', desc: 'Zamestnanec bloček odfotí, údaje z eKasa prídu priamo z Finančnej správy.' },
  { icon: ICONS.check, title: 'Objednávky', desc: 'Nákup sa schváli vopred. Keď príde faktúra, sama sa spáruje s objednávkou.' },
  { icon: ICONS.file, title: 'Vystavovanie faktúr', desc: 'Faktúry, zálohy a dobropisy s QR platbou PAY by square.' },
  { icon: ICONS.card, title: 'Banka', desc: 'Pohyby z účtu sa načítajú samé a spárujú sa s faktúrami.' },
  { icon: ICONS.receipt, title: 'Účtovníctvo', desc: 'Predkontácie, export do Omegy a mesačný balík pre účtovníčku.' },
  { icon: ICONS.calendar, title: 'Financie', desc: 'Kto vám dlží, koľko zaplatíte na DPH a odhad dane z príjmu.' },
];

export const FEATURES_EXTRA = 'Notifikácie v telefóne, e-mailom aj v Slacku · viac firiem pod jedným prihlásením · roly a audit log';

export const EFAKTURA = {
  changes: [
    'Platitelia DPH posielajú aj prijímajú faktúry ako XML',
    'Namiesto e-mailu ich doručuje digitálny poštár (Peppol)',
    'Údaje automaticky dostáva Finančná správa',
  ],
  ours: [
    'Prijatá e-faktúra ide rovno do schvaľovania',
    'Schválenie alebo zamietnutie sa vráti dodávateľovi',
    'Vystavené faktúry odošlete jedným tlačidlom',
    'Poštára vybavíme za vás',
  ],
};

const FEATURES_SOON: Array<{ icon: string; title: string; desc: string; featured?: boolean }> = [
  { icon: ICONS.link, title: 'Pohoda, Money S3', desc: 'Export aj do ďalších účtovných programov.' },
  { icon: ICONS.bolt, title: 'Platba na klik', desc: 'Hromadný platobný príkaz zo schválených faktúr.' },
  { icon: ICONS.chat, title: 'Microsoft Teams, WhatsApp', desc: 'Ďalšie kanály pre notifikácie.' },
];

const PROCESS = [
  { title: 'Vyžiadajte si prístup', desc: 'Skús plán si rozbehnete sami za pár minút. Pre platené plány vyplníte krátky formulár — ozveme sa do 24 h a spustíme vám účet aj prvotné nastavenie.' },
  { title: 'Nahrajte prvý doklad', desc: 'Ťahajte PDF do okna, pošlite mailom na adresu vašej firmy alebo odfoťte mobilom. AI ho prečíta za pár desiatok sekúnd.' },
  { title: 'Schválenie cez kliknutie', desc: 'Schvaľovatelia dostanú notifikáciu. Kliknú, pozrú, schvália alebo zamietnu s poznámkou.' },
  { title: 'Export do účtovníctva', desc: 'Účtovník stiahne mesačný balík dokladov v správnom formáte. Hotovo.' },
];

export const FAQS = [
  {
    q: 'Naozaj zadarmo? V čom je háčik?',
    a: 'Žiadny. Plán Skús (10 dokladov mesačne, 2 používatelia) je zadarmo navždy. Bez platobnej karty pri registrácii. Až keď budete chcieť viac dokladov alebo používateľov, prepnete sa na platený plán — môžete kedykoľvek.',
  },
  {
    q: 'Ako rýchlo vieme rozbehnúť firmu?',
    a: 'Skús plán si rozbehnete sami v priebehu pár minút. Pre platené plány si vyžiadate prístup formulárom a my vám účet spustíme do 24 hodín — vrátane prvotného nastavenia. Onboarding (online alebo osobný) je v cene každého plánu.',
  },
  {
    q: 'Sú moje dáta v bezpečí?',
    a: 'Áno. Dáta hostujeme v EÚ, šifrované pri prenose (TLS 1.3) aj v pokoji (AES-256). Denné zálohy. GDPR-compliant. Pri pláne Firma+ a Enterprise vám dodáme spracovateľskú zmluvu (DPA) a custom SLA.',
  },
  {
    q: 'Čo ak prerastiem free plán uprostred mesiaca?',
    a: 'Nič sa nestane — staré doklady zostávajú prístupné. Buď upgrade-nete, alebo počkáte do nového mesiaca. Žiadne pokuty, žiadne zamknutie dát.',
  },
  {
    q: 'Spolupracujete s mojím účtovníkom?',
    a: 'Áno. Účtovník má vlastný prístup, schválené doklady mu chodia mailom a export do Omegy, FLOWii či CSV stiahne jedným klikom. Pohodu a Money S3 pripravujeme.',
  },
  {
    q: 'Týka sa ma povinná e-fakturácia od 2027?',
    a: 'Ak ste platiteľ DPH, áno. Od 1. 1. 2027 posielate aj prijímate faktúry od iných firiem ako XML cez digitálneho poštára. PDF e-mailom už nestačí. Klientov na poštára prepájame do konca roka.',
  },
  {
    q: 'Musím si digitálneho poštára vyberať a platiť sám?',
    a: 'Nie, poštára vybavíme za vás. Vy len odsúhlasíte prepojenie firmy. Ak už poštára máte, napíšte nám.',
  },
  {
    q: 'Viem zbierať bločky od zamestnancov?',
    a: 'Áno. Zamestnanec si pridá appku na plochu telefónu a bloček odfotí. Údaje z eKasa prídu priamo z Finančnej správy.',
  },
  {
    q: 'Robíte aj on-premise / vlastný server?',
    a: 'Pre Enterprise plán áno — viete dostať inštanciu na vlastnej infraštruktúre alebo dedikovaný cloud v EÚ. Pište nám na hello@schvalovaniefaktur.sk a dohodneme detaily.',
  },
];

type Billing = 'monthly' | 'yearly';

export const PRICING: Array<{
  key: string;
  name: string;
  monthly: number | null;
  yearly: number | null;
  tag: string;
  ribbon?: string;
  featured?: boolean;
  cta: string;
  ctaTo: string;
  features: Array<{ text: string; muted?: boolean }>;
}> = [
  {
    key: 'free',
    name: 'Skús',
    monthly: 0,
    yearly: 0,
    tag: 'Pre živnostníkov a malé firmy, ktoré chcú vidieť, ako to chodí.',
    cta: 'Vyskúšať zadarmo',
    ctaTo: '/register',
    features: [
      { text: 'Do 10 dokladov mesačne' },
      { text: '2 používatelia' },
      { text: '1-úrovňové schvaľovanie' },
      { text: 'Sklad dokladov + filtre' },
      { text: 'Export CSV pre účtovníka' },
      { text: 'Online onboarding zdarma (30 min)' },
      { text: 'E-mailová podpora', muted: true },
    ],
  },
  {
    key: 'start',
    name: 'Štart',
    monthly: 12,
    yearly: 9,
    tag: 'Štandardný balík pre fungujúcu firmu s pár schvaľovateľmi.',
    cta: 'Vyžiadať prístup',
    ctaTo: 'mailto:hello@schvalovaniefaktur.sk?subject=Z%C3%A1ujem%20o%20pl%C3%A1n%20%C5%A0tart&body=Dobr%C3%BD%20de%C5%88%2C%0A%0Am%C3%A1m%20z%C3%A1ujem%20o%20pl%C3%A1n%20%C5%A0tart.%0A%0AFirma%3A%0AI%C4%8CO%3A%0AKontakt%C3%A1%20osoba%3A%0ATelef%C3%B3n%3A%0A%0A%C4%8Eakujem%2C',
    features: [
      { text: 'Do 100 dokladov mesačne' },
      { text: '5 používateľov' },
      { text: 'Viacúrovňové workflow' },
      { text: 'Notifikácie: push, e-mail, Slack' },
      { text: 'Pripomienky splatnosti' },
      { text: 'Export do Omegy, FLOWii, CSV' },
      { text: 'Online onboarding zdarma (60 min)' },
      { text: 'E-mailová podpora do 1 prac. dňa' },
    ],
  },
  {
    key: 'biznis',
    name: 'Biznis',
    monthly: 39,
    yearly: 29,
    tag: 'Pre firmy s viacerými strediskami alebo veľa dokladmi.',
    ribbon: 'Najobľúbenejšie',
    featured: true,
    cta: 'Vyžiadať prístup',
    ctaTo: 'mailto:hello@schvalovaniefaktur.sk?subject=Z%C3%A1ujem%20o%20pl%C3%A1n%20Biznis&body=Dobr%C3%BD%20de%C5%88%2C%0A%0Am%C3%A1m%20z%C3%A1ujem%20o%20pl%C3%A1n%20Biznis.%0A%0AFirma%3A%0AI%C4%8CO%3A%0AKontakt%C3%A1%20osoba%3A%0ATelef%C3%B3n%3A%0A%0A%C4%8Eakujem%2C',
    features: [
      { text: 'Do 500 dokladov mesačne' },
      { text: '15 používateľov' },
      { text: 'Pravidlá podľa sumy / strediska' },
      { text: 'Audit log a história zmien' },
      { text: 'API prístup pre integrácie' },
      { text: 'Prioritný support (4h SLA)' },
      { text: 'Osobný alebo online onboarding zdarma' },
    ],
  },
  {
    key: 'firma',
    name: 'Firma+',
    monthly: 89,
    yearly: 69,
    tag: 'Pre účtovné kancelárie a firmy nad 500 dokladov mesačne.',
    cta: 'Vyžiadať prístup',
    ctaTo: 'mailto:hello@schvalovaniefaktur.sk?subject=Z%C3%A1ujem%20o%20pl%C3%A1n%20Firma%2B&body=Dobr%C3%BD%20de%C5%88%2C%0A%0Am%C3%A1m%20z%C3%A1ujem%20o%20pl%C3%A1n%20Firma%2B.%0A%0AFirma%3A%0AI%C4%8CO%3A%0AKontakt%C3%A1%20osoba%3A%0ATelef%C3%B3n%3A%0A%0A%C4%8Eakujem%2C',
    features: [
      { text: 'Do 2 000 dokladov mesačne' },
      { text: '50 používateľov' },
      { text: 'Vlastné branding (logo, doména)' },
      { text: 'SSO (Google Workspace, Microsoft)' },
      { text: 'DPA a rozšírené GDPR podklady' },
      { text: 'Telefonický support, dedikovaný kontakt' },
      { text: 'Osobný onboarding zdarma' },
    ],
  },
];

const SEGMENTS = [
  { emoji: '🧑‍💻', title: 'Živnostník / freelancer', desc: 'Pár dokladov mesačne a nechce sa v tom topiť. Nahodí faktúru, schváli, pošle účtovníčke.', plan: 'Free plán stačí' },
  { emoji: '🏢', title: 'Malá aj väčšia firma', desc: 'Konateľ, vedúci stredísk či projektoví manažéri schvaľujú, účtovníčka spracúva. Koniec papierov na stole a „schválil si to?“ cez telefón.', plan: 'Štart / Biznis' },
  { emoji: '📊', title: 'Účtovná kancelária', desc: 'Spravuje desiatky firiem. Jedna appka, jedno prihlásenie, prehľad o všetkom, export rovno do Omegy.', plan: 'Firma+ / Enterprise' },
  { emoji: '🏗️', title: 'Viac stredísk / ERP', desc: 'Schvaľovanie podľa sumy a strediska, audit log, API napojenie na interné systémy.', plan: 'Biznis / Enterprise' },
];

const INTEGRATIONS: Array<{ name: string; live: boolean }> = [
  { name: 'Omega', live: true },
  { name: 'FLOWii', live: true },
  { name: 'CSV / XML / PDF', live: true },
  { name: 'Banka cez PSD2', live: true },
  { name: 'eKasa (Finančná správa)', live: true },
  { name: 'Obchodný register', live: true },
  { name: 'PAY by square', live: true },
  { name: 'Email', live: true },
  { name: 'Push v telefóne', live: true },
  { name: 'Slack', live: true },
  { name: 'Peppol e-faktúra', live: false },
  { name: 'POHODA', live: false },
  { name: 'Money S3', live: false },
  { name: 'Microsoft Teams', live: false },
  { name: 'WhatsApp', live: false },
];

const COMPARE_BEFORE = [
  'Faktúra obieha v maili — nikto nevie, u koho práve je',
  'Konateľ schvaľuje „ústne“ pri káve, dôkaz žiadny',
  'Účtovníčka loví prílohy z piatich mailboxov',
  'Po splatnosti zistíš až z upomienky a penále',
  'Žiadna história — kto, kedy a čo vlastne schválil?',
  'Audit alebo kontrola = panika a hľadanie šanónov',
];

const COMPARE_AFTER = [
  'Každý doklad má jasný stav a jedno miesto',
  'Schválenie na klik, s notifikáciou a stopou v logu',
  'Mesačný balík pre účtovníka jedným tlačidlom',
  'Upozornenie pár dní pred splatnosťou',
  'Nezmazateľný audit log — kto, kedy, čo, prečo',
  'Audit = stiahneš report za 10 sekúnd',
];

const TESTIMONIALS = [
  { quote: 'Konateľ mi prestal volať „schválil si tú faktúru?“. Vidí to sám v appke a klikne. My máme pokoj.', name: 'Martin K.', role: 'konateľ · stavebná firma, Žilina', color: 'linear-gradient(135deg,#2563eb,#1d4ed8)' },
  { quote: 'Účtovníčka ušetrí dva dni mesačne na zbieraní príloh z mailov. Doslova dva dni. Vrátilo sa nám to hneď.', name: 'Lucia H.', role: 'office manager · e-shop, Bratislava', color: 'linear-gradient(135deg,#7c3aed,#a78bfa)' },
  { quote: 'Spravujem 18 firiem. Predtým 18 mailboxov a chaos. Teraz jeden prehľad a export rovno do Omegy.', name: 'Peter D.', role: 'účtovná kancelária, Košice', color: 'linear-gradient(135deg,#0891b2,#22d3ee)' },
];

const SECURITY = [
  { icon: 'globe', title: 'Dáta v EÚ', desc: 'Hostujeme výhradne v dátacentrách v Európskej únii. Žiadne prenosy mimo EÚ.' },
  { icon: 'shield', title: 'Šifrovanie end-to-end', desc: 'TLS 1.3 pri prenose, AES-256 v pokoji. Vaše doklady nikto nečíta.' },
  { icon: 'database', title: 'Denné zálohy', desc: 'Automatické zálohovanie každý deň s možnosťou obnovy. Dáta nestratíte.' },
  { icon: 'file', title: 'Nezmazateľný audit log', desc: 'Každá akcia má stopu. Ideálne pre účtovníctvo, daňovú aj internú kontrolu.' },
  { icon: 'users', title: 'Granulárne role', desc: 'Presne nastavíte, kto čo vidí a smie schváliť. Žiadne zbytočné prístupy.' },
  { icon: 'check', title: 'GDPR + DPA', desc: 'Plne v súlade s GDPR. Spracovateľskú zmluvu (DPA) dodáme na vyžiadanie.' },
];

const STATS = [
  { prefix: '−', value: 75, decimals: 0, suffix: ' %', lbl: 'menej času stráveného schvaľovaním než papier a e-mail' },
  { prefix: '< ', value: 60, decimals: 0, suffix: ' s', lbl: 'priemerný čas schválenia jedného dokladu' },
  { prefix: '', value: 2, decimals: 0, suffix: ' dni', lbl: 'mesačne ušetrí účtovníčka na zbieraní príloh z mailov' },
  { prefix: '', value: 100, decimals: 0, suffix: ' %', lbl: 'dokladov s nezmazateľnou audit stopou' },
];

function CountUp({ prefix = '', value, decimals = 0, suffix = '' }: { prefix?: string; value: number; decimals?: number; suffix?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  // Pri vykreslení pri builde (scripts/prerender.js, bez window) rovno cieľové
  // číslo — bot nespúšťa JS a inak by prečítal nulu. V prehliadači od 0 s animáciou.
  const [display, setDisplay] = useState(() => (typeof window === 'undefined' ? value : 0));

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced || !('IntersectionObserver' in window)) {
      setDisplay(value);
      return;
    }
    let raf = 0;
    let started = false;
    let safety = 0;
    const run = () => {
      if (started) return;
      started = true;
      const dur = 1200;
      const t0 = performance.now();
      const tick = (now: number) => {
        const p = Math.min(1, (now - t0) / dur);
        const eased = 1 - Math.pow(1 - p, 3);
        setDisplay(value * eased);
        if (p < 1) raf = requestAnimationFrame(tick);
        else setDisplay(value);
      };
      raf = requestAnimationFrame(tick);
      // Poistka — ak je rAF throttled (skrytý tab/headless), dorač finálnu hodnotu
      safety = window.setTimeout(() => setDisplay(value), dur + 400);
    };
    const inView = () => {
      const r = el.getBoundingClientRect();
      return r.top < window.innerHeight * 0.9 && r.bottom > 0;
    };
    if (inView()) run();
    const io = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) { run(); io.disconnect(); } }),
      { threshold: 0.4 }
    );
    io.observe(el);
    const onScroll = () => { if (inView()) { run(); window.removeEventListener('scroll', onScroll); } };
    window.addEventListener('scroll', onScroll, { passive: true });
    // Absolútna poistka — číslo nikdy neostane na 0
    const hardSafety = window.setTimeout(() => setDisplay(value), 3500);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.clearTimeout(safety);
      window.clearTimeout(hardSafety);
    };
  }, [value]);

  const formatted = display.toLocaleString('sk-SK', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return <span ref={ref} className="grad">{prefix}{formatted}{suffix}</span>;
}

/* ============================================================
   Interaktívny mockup dashboardu (živá ukážka v hero sekcii)
   ============================================================ */

type DocStatus = 'approved' | 'pending' | 'review' | 'overdue';
type MockTab = 'all' | 'pending' | 'review' | 'approved';

interface DocItem {
  id: string;
  vendor: string;
  amount: string;
  due: string;
  status: DocStatus;
  invoiceNo: string;
  iban: string;
  subject: string;
  approver?: string;
  approvedAt?: string;
}

const INITIAL_DOCS: DocItem[] = [
  { id: 'd1', vendor: 'O2 Slovakia, s.r.o.', amount: '248,40 €', due: '12. 5. 2026', status: 'approved', invoiceNo: 'VF20260512-001', iban: 'SK12 1100 0000 0029 4012 3456', subject: 'Mobilné služby — máj 2026', approver: 'Martin Konateľ', approvedAt: '11. 5. 2026, 14:32' },
  { id: 'd2', vendor: 'Alza.sk s.r.o.', amount: '1 412,00 €', due: '15. 5. 2026', status: 'pending', invoiceNo: 'AZ-2026-04711', iban: 'SK45 1100 0000 0028 9876 5432', subject: 'Notebooky Dell Latitude 5 ks' },
  { id: 'd3', vendor: 'SPP — distribúcia, a.s.', amount: '89,15 €', due: '20. 5. 2026', status: 'review', invoiceNo: 'SPP/2026/0512', iban: 'SK77 0900 0000 0001 2345 6789', subject: 'Distribúcia zemného plynu' },
  { id: 'd4', vendor: 'DHL Express (Slovakia)', amount: '74,90 €', due: '5. 5. 2026', status: 'overdue', invoiceNo: 'DHL-20260428', iban: 'SK23 0200 0000 0001 1122 3344', subject: 'Prepravné — apríl 2026' },
  { id: 'd5', vendor: 'Slovak Telekom, a.s.', amount: '129,00 €', due: '28. 5. 2026', status: 'approved', invoiceNo: 'ST-2026-05-119', iban: 'SK56 1100 0000 0026 1119 9999', subject: 'Internet + pevná linka', approver: 'Jana Veselá (účtovníčka)', approvedAt: '13. 5. 2026, 09:15' },
  { id: 'd6', vendor: 'Kaufland Slovensko', amount: '342,18 €', due: '8. 5. 2026', status: 'pending', invoiceNo: 'KF-2026/A/1842', iban: 'SK91 7500 0000 0000 0011 2233', subject: 'Občerstvenie na firemnú akciu' },
];

function nowStamp() {
  try {
    return new Date().toLocaleString('sk-SK', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return 'práve teraz';
  }
}

function StatusPill({ status }: { status: DocStatus }) {
  const map: Record<DocStatus, { cls: string; label: string }> = {
    approved: { cls: 'ok', label: 'Schválené' },
    pending: { cls: 'pending', label: 'Čaká' },
    review: { cls: 'review', label: 'Kontrola' },
    overdue: { cls: 'over', label: 'Po splatnosti' },
  };
  const m = map[status];
  return <span className={`pill ${m.cls}`}>{m.label}</span>;
}

function InteractiveMockup() {
  const [docs, setDocs] = useState<DocItem[]>(INITIAL_DOCS);
  const [tab, setTab] = useState<MockTab>('all');
  const [expandedId, setExpandedId] = useState<string | null>('d2');

  const counts = useMemo(() => ({
    all: docs.length,
    pending: docs.filter(d => d.status === 'pending' || d.status === 'overdue').length,
    review: docs.filter(d => d.status === 'review').length,
    approved: docs.filter(d => d.status === 'approved').length,
  }), [docs]);

  const filtered = useMemo(() => docs.filter(d => {
    if (tab === 'all') return true;
    if (tab === 'pending') return d.status === 'pending' || d.status === 'overdue';
    if (tab === 'review') return d.status === 'review';
    if (tab === 'approved') return d.status === 'approved';
    return true;
  }), [docs, tab]);

  const setStatus = (id: string, status: DocStatus) => {
    setDocs(prev => prev.map(d => {
      if (d.id !== id) return d;
      if (status === 'approved') {
        return { ...d, status, approver: 'Vy (Martin Konateľ)', approvedAt: nowStamp() };
      }
      return { ...d, status, approver: undefined, approvedAt: undefined };
    }));
    // Necháme riadok rozbalený, nech používateľ hneď vidí výsledok (kto/kedy)
  };

  const tabs: Array<{ key: MockTab; label: string; icon: string; count: number }> = [
    { key: 'all', label: 'Prijaté doklady', icon: '📥', count: counts.all },
    { key: 'pending', label: 'Na schválenie', icon: '⏳', count: counts.pending },
    { key: 'review', label: 'Kontrola', icon: '🔍', count: counts.review },
    { key: 'approved', label: 'Schválené', icon: '✅', count: counts.approved },
  ];

  return (
    <div className="hero-mock">
      <div className="mock-card">
        <div className="mock-bar">
          <span className="mock-dot r" /><span className="mock-dot y" /><span className="mock-dot g" />
          <span className="mock-url">app.schvalovaniefaktur.sk / dashboard</span>
        </div>
        <div className="mock-grid">
          <aside className="mock-side">
            <h5>Doklady</h5>
            <ul>
              {tabs.map(t => (
                <li
                  key={t.key}
                  className={tab === t.key ? 'active' : ''}
                  onClick={() => { setTab(t.key); setExpandedId(null); }}
                >
                  <span>{t.icon} {t.label}</span>
                  <span className="badge-count">{t.count}</span>
                </li>
              ))}
            </ul>
            <h5 className="sub">Workspace</h5>
            <ul>
              <li className="passive"><span>🏢 Firmy</span></li>
              <li className="passive"><span>👥 Tím</span></li>
              <li className="passive"><span>📤 Export</span></li>
            </ul>
          </aside>
          <div className="mock-table">
            <div className="mock-row header">
              <span>Dodávateľ</span>
              <span>Suma</span>
              <span className="col-due">Splatnosť</span>
              <span>Stav</span>
            </div>
            {filtered.length === 0 && (
              <div className="mock-empty">Žiadne doklady v tejto kategórii.</div>
            )}
            {filtered.map(doc => (
              <React.Fragment key={doc.id}>
                <div
                  className={`mock-row clickable ${expandedId === doc.id ? 'expanded' : ''}`}
                  onClick={() => setExpandedId(prev => (prev === doc.id ? null : doc.id))}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setExpandedId(prev => (prev === doc.id ? null : doc.id));
                    }
                  }}
                >
                  <span>{doc.vendor}</span>
                  <span>{doc.amount}</span>
                  <span className="col-due">{doc.due}</span>
                  <span><StatusPill status={doc.status} /></span>
                </div>
                {expandedId === doc.id && (
                  <div className="mock-detail">
                    <div className="mock-detail-grid">
                      <div><label>Číslo faktúry</label><span>{doc.invoiceNo}</span></div>
                      <div><label>IBAN</label><span style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: 12.5 }}>{doc.iban}</span></div>
                      <div><label>Splatnosť</label><span>{doc.due}</span></div>
                      <div className="span-2"><label>Predmet</label><span>{doc.subject}</span></div>
                      <div><label>Suma</label><span style={{ fontWeight: 700 }}>{doc.amount}</span></div>
                    </div>
                    {doc.status === 'approved' && doc.approver && (
                      <div className="mock-approval ok">
                        <span>✓</span><span>Schválil(a) <strong>{doc.approver}</strong> · {doc.approvedAt}</span>
                      </div>
                    )}
                    {doc.status === 'review' && (
                      <div className="mock-approval review">
                        <span>↩</span><span>Vrátené na kontrolu — čaká na doplnenie podkladov</span>
                      </div>
                    )}
                    {(doc.status === 'pending' || doc.status === 'overdue') && (
                      <div className="mock-approval wait">
                        <span>⏳</span><span>Čaká na schválenie {doc.status === 'overdue' ? '· doklad je už po splatnosti' : ''}</span>
                      </div>
                    )}
                    <div className="mock-actions">
                      {doc.status !== 'approved' && (
                        <button
                          className="mock-btn primary"
                          onClick={(e) => { e.stopPropagation(); setStatus(doc.id, 'approved'); }}
                        >
                          ✓ Schváliť
                        </button>
                      )}
                      {doc.status !== 'review' && (
                        <button
                          className="mock-btn ghost"
                          onClick={(e) => { e.stopPropagation(); setStatus(doc.id, 'review'); }}
                        >
                          Vrátiť na kontrolu
                        </button>
                      )}
                      {doc.status === 'approved' && (
                        <button
                          className="mock-btn danger"
                          onClick={(e) => { e.stopPropagation(); setStatus(doc.id, 'pending'); }}
                        >
                          Zrušiť schválenie
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
      <p className="mock-hint">
        ↑ Toto je <strong style={{ color: 'var(--text)' }}>živá ukážka</strong> — skús kliknúť na riadok alebo prepínať <span className="kbd">karty vľavo</span>.
      </p>
    </div>
  );
}

/* ============================================================
   Theme toggle button
   ============================================================ */
function ThemeToggle({ theme, onToggle }: { theme: 'light' | 'dark'; onToggle: () => void }) {
  return (
    <button
      type="button"
      className="theme-toggle"
      onClick={onToggle}
      aria-label={theme === 'light' ? 'Prepnúť na tmavú tému' : 'Prepnúť na svetlú tému'}
      title={theme === 'light' ? 'Tmavá téma' : 'Svetlá téma'}
    >
      {theme === 'light' ? (
        /* Moon icon — when light, click to go dark */
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      ) : (
        /* Sun icon — when dark, click to go light */
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2 M12 20v2 M4.93 4.93l1.41 1.41 M17.66 17.66l1.41 1.41 M2 12h2 M20 12h2 M4.93 19.07l1.41-1.41 M17.66 6.34l1.41-1.41" />
        </svg>
      )}
    </button>
  );
}

/* ============================================================
   Landing page
   ============================================================ */

const NAV_LINKS = [
  { href: '#funkcie', label: 'Funkcie' },
  { href: '#efaktura', label: 'E-faktúra' },
  { href: '#proces', label: 'Ako to funguje' },
  { href: '#cennik', label: 'Cenník' },
  { href: '#referencie', label: 'Referencie' },
  { href: '#faq', label: 'FAQ' },
];

export default function Landing() {
  const [billing, setBilling] = useState<Billing>('yearly');
  const [menuOpen, setMenuOpen] = useState(false);
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    try {
      const saved = localStorage.getItem('landing-theme');
      if (saved === 'dark' || saved === 'light') return saved;
    } catch {}
    return 'light';
  });

  useEffect(() => {
    document.body.classList.add('landing-body');
    initAnalytics();
    return () => {
      document.body.classList.remove('landing-body');
      document.documentElement.removeAttribute('data-theme');
    };
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try { localStorage.setItem('landing-theme', theme); } catch {}
  }, [theme]);

  // Scroll-reveal — odhalí prvky s .reveal. Robustné: prvky vo viewporte
  // sa odhalia okamžite (žiadny FOUC), zvyšok cez IntersectionObserver,
  // plus scroll + timeout fallback, aby obsah nikdy nezostal neviditeľný.
  useEffect(() => {
    const els = Array.from(document.querySelectorAll<HTMLElement>('.reveal'));
    if (els.length === 0) return;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced || !('IntersectionObserver' in window)) {
      els.forEach(el => el.classList.add('in'));
      return;
    }

    const reveal = (el: Element) => el.classList.add('in');
    const inView = (el: Element) => {
      const r = el.getBoundingClientRect();
      return r.top < window.innerHeight * 0.95 && r.bottom > 0;
    };

    // 1) Okamžite odhaľ čokoľvek, čo je už vo viewporte (above-the-fold)
    els.forEach(el => { if (inView(el)) reveal(el); });

    // 2) IntersectionObserver pre zvyšok (pekný staggered efekt pri scrolle)
    const io = new IntersectionObserver(
      (entries) => entries.forEach(entry => {
        if (entry.isIntersecting) { reveal(entry.target); io.unobserve(entry.target); }
      }),
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    els.forEach(el => { if (!el.classList.contains('in')) io.observe(el); });

    // 3) Scroll fallback (ak by IO mlčal)
    const onScroll = () => {
      let remaining = false;
      els.forEach(el => {
        if (!el.classList.contains('in')) {
          if (inView(el)) reveal(el);
          else remaining = true;
        }
      });
      if (!remaining) window.removeEventListener('scroll', onScroll);
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    // 4) Posledná poistka — nikdy nenechaj obsah skrytý
    const safety = window.setTimeout(() => els.forEach(reveal), 2600);

    return () => {
      io.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.clearTimeout(safety);
    };
  }, []);

  const toggleTheme = () => setTheme(t => (t === 'light' ? 'dark' : 'light'));
  const closeMenu = () => setMenuOpen(false);

  // Zavri mobilné menu na Escape a po zväčšení okna na desktop
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    const onResize = () => { if (window.innerWidth > 880) setMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [menuOpen]);

  return (
    <div className="landing">
      <style>{styles}</style>

      {/* NAV */}
      <header className="nav">
        <div className="container nav-inner">
          <a className="brand" href="#top" onClick={closeMenu}>
            <span className="brand-mark" aria-hidden>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l4 4L19 7" /></svg>
            </span>
            <span className="brand-text">schvalovaniefaktur<span className="dot">.sk</span></span>
          </a>
          <nav className="nav-links" aria-label="Hlavné menu">
            {NAV_LINKS.map(l => <a key={l.href} href={l.href}>{l.label}</a>)}
          </nav>
          <div className="nav-cta">
            <ThemeToggle theme={theme} onToggle={toggleTheme} />
            <a href={LOGIN_URL} className="btn btn-ghost nav-desktop-only">Prihlásiť sa</a>
            <a href={REGISTER_URL} className="btn btn-primary nav-desktop-only" onClick={() => track('cta_free', { location: 'nav' })}>Skúsiť zadarmo</a>
            <button
              className="hamburger"
              aria-label={menuOpen ? 'Zavrieť menu' : 'Otvoriť menu'}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(o => !o)}
            >
              <span className={`hb-box ${menuOpen ? 'open' : ''}`}><i /><i /><i /></span>
            </button>
          </div>
        </div>
        <div className={`mobile-menu ${menuOpen ? 'open' : ''}`}>
          <div className="container mm-inner">
            {NAV_LINKS.map(l => (
              <a key={l.href} href={l.href} onClick={closeMenu}>{l.label}</a>
            ))}
            <div className="mm-cta">
              <a href={LOGIN_URL} className="btn btn-ghost btn-block" onClick={closeMenu}>Prihlásiť sa</a>
              <a href={REGISTER_URL} className="btn btn-primary btn-block" onClick={closeMenu}>Skúsiť zadarmo</a>
            </div>
          </div>
        </div>
      </header>

      <main id="top">
        {/* HERO */}
        <section className="hero">
          <div className="container hero-inner">
            <a href="#efaktura" className="badge" style={{ textDecoration: 'none' }}>
              <span className="pulse" aria-hidden /> Pripravujeme sa na e-fakturáciu 2027 →
            </a>
            <h1 className="hero-title">
              Schvaľovanie faktúr.<br />
              <span className="grad">Bez papierov. Bez stresu.</span>
            </h1>
            <p className="hero-sub">
              Faktúry, bločky a objednávky na jednom mieste. AI ich prečíta, ľudia schvália na klik a účtovníčka dostane export do Omegy.
            </p>
            <div className="hero-cta">
              <a href={REGISTER_URL} className="btn btn-primary btn-lg" onClick={() => track('cta_free', { location: 'hero' })}>
                Skúsiť zadarmo · do 10 dokladov / mes.
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14" /><path d="M12 5l7 7-7 7" /></svg>
              </a>
              <a href="#funkcie" className="btn btn-ghost btn-lg">Pozrieť, čo to vie</a>
            </div>
            <div className="hero-trust">
              <span><span className="check">✓</span> Bez platobnej karty</span>
              <span><span className="check">✓</span> Onboarding zdarma</span>
              <span><span className="check">✓</span> Dáta v EÚ, GDPR</span>
              <span><span className="check">✓</span> Slovenská podpora</span>
            </div>
          </div>

          <div className="container">
            <InteractiveMockup />
          </div>
        </section>

        {/* STATS */}
        <section className="stats">
          <div className="container">
            <div className="stats-grid">
              {STATS.map((s, i) => (
                <div className="stat reveal" key={i} style={{ transitionDelay: `${i * 70}ms` }}>
                  <div className="num">
                    <CountUp prefix={s.prefix} value={s.value} decimals={s.decimals} suffix={s.suffix} />
                  </div>
                  <div className="lbl">{s.lbl}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PRE KOHO TO JE */}
        <section id="pre-koho">
          <div className="container">
            <div className="section-head reveal">
              <span className="eyebrow">Pre koho to je</span>
              <h2 className="section-title">Spoznáte sa? Tak je to pre vás</h2>
              <p className="section-lead">Od jednej faktúry mesačne po desiatky firiem v účtovnej kancelárii — sadne to každému, kto rieši prijaté doklady.</p>
            </div>
            <div className="segments-grid">
              {SEGMENTS.map((s, i) => (
                <div className="segment reveal" key={i} style={{ transitionDelay: `${i * 70}ms` }}>
                  <div className="seg-emoji">{s.emoji}</div>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                  <span className="seg-plan">{s.plan}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="section-divider" />

        {/* FEATURES — LIVE */}
        <section id="funkcie">
          <div className="container">
            <div className="section-head reveal">
              <span className="eyebrow">Moduly</span>
              <h2 className="section-title">Od objednávky po zaúčtovanie</h2>
              <p className="section-lead">Všetky moduly už bežia u našich klientov. Zapnete si tie, ktoré potrebujete.</p>
            </div>
            <div className="features-grid four">
              {FEATURES_LIVE.map((f, i) => (
                <div className="feature reveal" key={i} style={{ transitionDelay: `${(i % 4) * 60}ms` }}>
                  <div className="feature-icon"><Icon path={f.icon} /></div>
                  <h3>{f.title}</h3>
                  <p>{f.desc}</p>
                </div>
              ))}
            </div>
            <p className="features-extra">{FEATURES_EXTRA}</p>
          </div>
        </section>

        {/* INTEGRATIONS STRIP */}
        <section style={{ paddingTop: 56, paddingBottom: 56 }}>
          <div className="container logos-wrap">
            <span className="eyebrow">Integrácie</span>
            <h2 className="section-title" style={{ fontSize: 'clamp(24px, 3vw, 34px)' }}>Zapadne do sveta, ktorý už používate</h2>
            <div className="logos-strip">
              {INTEGRATIONS.map((g, i) => (
                <span className="logo-badge reveal" key={i} style={{ transitionDelay: `${i * 40}ms` }}>
                  <span className={g.live ? 'live-dot' : 'soon-dot'} />
                  {g.name}
                </span>
              ))}
            </div>
            <p className="logos-note">
              <span style={{ color: 'var(--success)' }}>●</span> dostupné teraz &nbsp;·&nbsp; <span style={{ color: 'var(--soon-fg)' }}>●</span> v príprave (pozri <a href="#pripravujeme" style={{ color: 'var(--accent)' }}>roadmapu</a>)
            </p>
          </div>
        </section>

        <div className="section-divider" />

        {/* E-FAKTURÁCIA 2027 */}
        <section id="efaktura">
          <div className="container">
            <div className="section-head reveal">
              <span className="eyebrow">E-fakturácia 2027</span>
              <h2 className="section-title">Od 1. 1. 2027 končí faktúra v PDF</h2>
              <p className="section-lead">Napojenie na digitálneho poštára máme otestované. Klientov prepájame do konca roka.</p>
            </div>
            <div className="compare-grid">
              <div className="compare-col info reveal">
                <h3 style={{ marginBottom: 16 }}>Čo sa mení</h3>
                <ul>
                  {EFAKTURA.changes.map((t, i) => (
                    <li key={i}><span className="ic">!</span><span>{t}</span></li>
                  ))}
                </ul>
              </div>
              <div className="compare-col after reveal" style={{ transitionDelay: '90ms' }}>
                <h3 style={{ marginBottom: 16 }}>Čo pre vás pripravujeme</h3>
                <ul>
                  {EFAKTURA.ours.map((t, i) => (
                    <li key={i}><span className="ic">✓</span><span>{t}</span></li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="enterprise">
              <div>
                <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: 'var(--text-strong)' }}>Chcete mať e-fakturáciu vybavenú včas? Napíšte nám IČO.</h3>
              </div>
              <a href="mailto:hello@schvalovaniefaktur.sk?subject=E-faktur%C3%A1cia%202027&body=Dobr%C3%BD%20de%C5%88%2C%0A%0Am%C3%A1m%20z%C3%A1ujem%20o%20e-faktur%C3%A1ciu%20cez%20schvalovaniefaktur.sk.%0A%0AFirma%3A%0AI%C4%8CO%3A%0AKontakt%3A%0A%0A%C4%8Eakujem%2C" className="btn btn-primary btn-lg" onClick={() => track('lead_request', { plan: 'E-faktura 2027', location: 'efaktura' })}>Chcem byť pripravený</a>
            </div>
          </div>
        </section>

        <div className="section-divider" />

        {/* COMING SOON */}
        <section id="pripravujeme">
          <div className="container">
            <div className="section-head">
              <span className="eyebrow">Roadmapa · pripravujeme</span>
              <h2 className="section-title">Na čom pracujeme</h2>
            </div>
            <div className="features-grid">
              {FEATURES_SOON.map((f, i) => (
                <div className={`feature reveal ${f.featured ? 'soon-featured' : ''}`} key={i} style={{ transitionDelay: `${(i % 3) * 60}ms` }}>
                  {f.featured && <span className="soon-ribbon">★ Najžiadanejšie</span>}
                  <div className="feature-icon soon"><Icon path={f.icon} /></div>
                  <h3>{f.title}</h3>
                  <p>{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="section-divider" />

        {/* PROCESS */}
        <section id="proces">
          <div className="container">
            <div className="section-head">
              <span className="eyebrow">Proces</span>
              <h2 className="section-title">Od registrácie po prvý schválený doklad za pár minút</h2>
              <p className="section-lead">Žiadne demo cally, žiadne integrácie cez týždne. Otvoríte si účet a pracujete.</p>
            </div>
            <div className="process-grid">
              {PROCESS.map((s, i) => (
                <div className="step reveal" key={i} style={{ transitionDelay: `${i * 80}ms` }}>
                  <div className="step-num">{i + 1}</div>
                  <h4>{s.title}</h4>
                  <p>{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="section-divider" />

        {/* PREDTÝM vs. S NAMI */}
        <section id="porovnanie">
          <div className="container">
            <div className="section-head reveal">
              <span className="eyebrow">Predtým vs. teraz</span>
              <h2 className="section-title">Ako vyzerá schvaľovanie bez nás a s nami</h2>
              <p className="section-lead">Možno vám to bude povedomé. Ľavá strana je dnešný deň väčšiny firiem.</p>
            </div>
            <div className="compare-grid">
              <div className="compare-col before reveal">
                <h3>😮‍💨 Predtým</h3>
                <p className="ch-sub">papier · email · excel · telefonáty</p>
                <ul>
                  {COMPARE_BEFORE.map((t, i) => (
                    <li key={i}><span className="ic">✕</span><span>{t}</span></li>
                  ))}
                </ul>
              </div>
              <div className="compare-col after reveal" style={{ transitionDelay: '90ms' }}>
                <h3>😌 S schvalovaniefaktur.sk</h3>
                <p className="ch-sub">jeden prehľad · jasné stavy · audit</p>
                <ul>
                  {COMPARE_AFTER.map((t, i) => (
                    <li key={i}><span className="ic">✓</span><span>{t}</span></li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <div className="section-divider" />

        {/* PRICING */}
        <section id="cennik">
          <div className="container">
            <div className="section-head">
              <span className="eyebrow">Cenník</span>
              <h2 className="section-title">Jasné ceny. Bez prekvapení. Bez záväzkov.</h2>
              <p className="section-lead">Začnete zadarmo, kedykoľvek upgrade-nete alebo zmeníte plán. Ceny sú bez DPH a za jednu firmu.</p>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div className="billing-toggle" role="tablist">
                <button className={billing === 'monthly' ? 'active' : ''} onClick={() => setBilling('monthly')}>Mesačne</button>
                <button className={billing === 'yearly' ? 'active' : ''} onClick={() => setBilling('yearly')}>
                  Ročne <span className="save-pill">až –25%</span>
                </button>
              </div>
            </div>
            {billing === 'yearly' && (
              <p style={{ textAlign: 'center', margin: '14px 0 0', color: 'var(--accent)', fontWeight: 600, fontSize: 14.5 }}>
                ✓ Pri ročnej platbe: prvotné nastavenie + onboarding zdarma vo všetkých plánoch
              </p>
            )}

            <div className="pricing-grid">
              {PRICING.map((p, idx) => {
                const price = billing === 'yearly' ? p.yearly : p.monthly;
                return (
                  <div className={`plan reveal ${p.featured ? 'featured' : ''}`} key={p.key} style={{ transitionDelay: `${idx * 70}ms` }}>
                    {p.ribbon && <span className="ribbon">{p.ribbon}</span>}
                    <span className="plan-name">{p.name}</span>
                    <div className="plan-price">
                      {price === 0 ? (
                        <span className="val">0 €</span>
                      ) : (
                        <>
                          <span className="val">{price} €</span>
                          <span className="per">/ mesiac{billing === 'yearly' ? ' (ročne)' : ''}</span>
                        </>
                      )}
                    </div>
                    <p className="plan-tag">{p.tag}</p>
                    <ul>
                      {p.features.map((f, i) => (
                        <li key={i}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                          <span className={f.muted ? 'muted' : ''}>{f.text}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="plan-cta">
                      <a href={p.ctaTo.startsWith('/') ? `${APP_URL}${p.ctaTo}` : p.ctaTo} className={`btn btn-block ${p.featured ? 'btn-primary' : 'btn-ghost'}`} onClick={() => track(p.key === 'free' ? 'cta_free' : 'lead_request', { plan: p.name, location: 'pricing' })}>{p.cta}</a>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Enterprise */}
            <div className="enterprise">
              <div>
                <span className="plan-name">Enterprise</span>
                <h3 style={{ margin: '6px 0 4px', fontSize: 22, fontWeight: 800, color: 'var(--text-strong)' }}>Bez limitov · vlastná inštancia · 24/7 SLA</h3>
                <p style={{ margin: 0, color: 'var(--muted)', fontSize: 14.5 }}>
                  Pre účtovné kancelárie nad 50 firiem, holdingy, štátnu správu a firmy s vlastnými compliance požiadavkami. On-premise alebo dedikovaný cloud v EÚ, SSO, custom integrácie, SLA podľa potreby, právne podklady (DPA, SCC), dedikovaný projekt manažér.
                </p>
              </div>
              <a href="mailto:hello@schvalovaniefaktur.sk?subject=Enterprise%20pl%C3%A1n" className="btn btn-primary btn-lg" onClick={() => track('lead_request', { plan: 'Enterprise', location: 'pricing' })}>Dohodnúť konzultáciu</a>
            </div>

            <p className="pricing-note">
              Všetky platené plány zahŕňajú: neobmedzený archív dokladov · denné zálohy · TLS 1.3 + AES-256 · audit log · update-y zadarmo<br />
              Doklady nad rámec plánu: 0,15 € / doklad (Štart), 0,10 € / doklad (Biznis), 0,06 € / doklad (Firma+)
            </p>
          </div>
        </section>

        <div className="section-divider" />

        {/* REFERENCIE */}
        <section id="referencie">
          <div className="container">
            <div className="section-head reveal">
              <span className="eyebrow">Referencie</span>
              <h2 className="section-title">Firmy, ktoré už majú pokoj</h2>
              <p className="section-lead">Pár hlasov z firiem, ktoré prešli z papierov a mailov na schvalovaniefaktur.sk.</p>
            </div>
            <div className="testi-grid">
              {TESTIMONIALS.map((t, i) => (
                <div className="testi reveal" key={i} style={{ transitionDelay: `${i * 80}ms` }}>
                  <div className="stars">★★★★★</div>
                  <blockquote>„{t.quote}“</blockquote>
                  <div className="testi-who">
                    <div className="testi-avatar" style={{ background: t.color }}>{t.name.charAt(0)}</div>
                    <div>
                      <div className="testi-name">{t.name}</div>
                      <div className="testi-role">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="section-divider" />

        {/* BEZPEČNOSŤ */}
        <section id="bezpecnost">
          <div className="container">
            <div className="section-head reveal">
              <span className="eyebrow">Bezpečnosť a súkromie</span>
              <h2 className="section-title">Vaše doklady sú citlivé. Berieme to vážne.</h2>
              <p className="section-lead">Žiadne US cloudy, žiadne čítanie vašich dát. Postavené tak, aby prešlo aj cez vášho IT-čkára a právnika.</p>
            </div>
            <div className="security-grid">
              {SECURITY.map((s, i) => (
                <div className="sec-item reveal" key={i} style={{ transitionDelay: `${(i % 3) * 70}ms` }}>
                  <div className="sec-icon"><Icon path={(ICONS as Record<string, string>)[s.icon]} size={20} /></div>
                  <div>
                    <h4>{s.title}</h4>
                    <p>{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="section-divider" />

        {/* FAQ */}
        <section id="faq">
          <div className="container">
            <div className="section-head">
              <span className="eyebrow">Často sa pýtate</span>
              <h2 className="section-title">FAQ</h2>
            </div>
            <div className="faq">
              {FAQS.map((item, i) => (
                <details className="reveal" key={i} {...(i === 0 ? { open: true } : {})}>
                  <summary>{item.q}</summary>
                  <div className="answer">{item.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section style={{ paddingTop: 32 }}>
          <div className="container">
            <div className="final-cta">
              <h2>Zbavte sa papierov ešte dnes</h2>
              <p>Skús plán si rozbehnete sami za pár minút. Bez platobnej karty. Prvých 10 dokladov mesačne navždy zadarmo. Onboarding zdarma vo všetkých plánoch.</p>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                <a href={REGISTER_URL} className="btn btn-primary btn-lg" onClick={() => track('cta_free', { location: 'final' })}>
                  Začať zadarmo
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14" /><path d="M12 5l7 7-7 7" /></svg>
                </a>
                <a href="mailto:hello@schvalovaniefaktur.sk" className="btn btn-ghost btn-lg" onClick={() => track('contact', { location: 'final' })}>Napíšte nám</a>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer>
        <div className="container">
          <div className="footer-grid">
            <div>
              <a className="brand" href="#top" style={{ marginBottom: 12 }}>
                <span className="brand-mark" aria-hidden>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l4 4L19 7" /></svg>
                </span>
                <span className="brand-text">schvalovaniefaktur<span className="dot">.sk</span></span>
              </a>
              <p style={{ marginTop: 10, maxWidth: 320 }}>
                Slovenský nástroj na schvaľovanie prijatých faktúr. Postavený lokálne, podpora v slovenčine, dáta v EÚ.
              </p>
            </div>
            <div>
              <h6>Produkt</h6>
              <a href="#funkcie">Funkcie</a>
              <a href="#efaktura">E-fakturácia 2027</a>
              <a href="#pripravujeme">Roadmapa</a>
              <a href="#cennik">Cenník</a>
              <a href="#proces">Ako to funguje</a>
            </div>
            <div>
              <h6>Spoločnosť</h6>
              <a href="mailto:hello@schvalovaniefaktur.sk">Kontakt</a>
              <a href="#faq">FAQ</a>
              <a href={LOGIN_URL}>Prihlásenie</a>
            </div>
            <div>
              <h6>Právne</h6>
              <a href="#top">Obchodné podmienky</a>
              <a href="#top">Ochrana osobných údajov</a>
              <a href="#top">Spracovanie dát (DPA)</a>
            </div>
          </div>
          <div className="footer-bottom">
            <span>© {new Date().getFullYear()} schvalovaniefaktur.sk · Vyrobené na Slovensku 🇸🇰</span>
            <span>
              Vytvorené spoločnosťou{' '}
              <a href="https://klikit.sk" target="_blank" rel="noopener noreferrer" style={{ fontWeight: 700 }}>KLIK IT</a>
              {' · '}hello@schvalovaniefaktur.sk · +421 910 520 846
            </span>
          </div>
        </div>
      </footer>

      <ConsentBanner />
    </div>
  );
}
